"use client"

import type React from "react"

import { useState, useEffect } from "react"
import { SessionProvider } from "next-auth/react"
import { AppRouterCacheProvider } from "@mui/material-nextjs/v14-appRouter"
import { ThemeProvider } from "@mui/material/styles"
import dynamic from "next/dynamic"
import theme from "@/theme"

// Lazy load analytics components
const Analytics = dynamic(() => import("@vercel/analytics/react").then((mod) => mod.Analytics), { ssr: false })

const SpeedInsights = dynamic(() => import("@vercel/speed-insights/next").then((mod) => mod.SpeedInsights), {
  ssr: false,
})

const ConsentDialog = dynamic(() => import("./consent/ConsentDialog"), {
  ssr: false,
  loading: () => null,
})

// Client-side consent initialization to avoid hydration mismatch
const ConsentInitializer = dynamic(() => import("./consent/ConsentInitializer"), {
  ssr: false,
})

export default function ClientProviders({ children }: { children: React.ReactNode }) {
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    // Defer mounting to avoid hydration issues
    setMounted(true)
  }, [])

  return (
    <SessionProvider refetchInterval={0} refetchOnWindowFocus={false} refetchWhenOffline={false}>
      <AppRouterCacheProvider options={{ enableCssLayer: true }}>
        <ThemeProvider theme={theme}>
          {children}

          {/* Initialize consent on client-side only */}
          {mounted && <ConsentInitializer />}

          {/* Always render ConsentDialog */}
          {mounted && <ConsentDialog />}

          {/* Render analytics only after mounting */}
          {mounted && (
            <>
              <Analytics />
              <SpeedInsights />
            </>
          )}
        </ThemeProvider>
      </AppRouterCacheProvider>
    </SessionProvider>
  )
}
